import { createTRPCContext } from '@trpc/tanstack-react-query'
import { createTRPCClient, httpBatchLink, TRPCClient } from '@trpc/client'
import type { AppRouter, AppRouterOutputs, AppRouterInputs } from '@willie/trpc-server'
import { QueryClient } from '@tanstack/react-query'
import { PropsWithChildren, useMemo } from 'react'
import { WillieSupabaseClient } from '@willie/supabase'
import { useAuth } from '~/auth/AuthProvider'

export type TRPCOutputs = AppRouterOutputs
export type TRPCInputs = AppRouterInputs
export type WillieTRPCClient = TRPCClient<AppRouter>

const { TRPCProvider: TRPCContextProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>()

// Node rejects a request line plus headers over 16 KiB with a 431; leaves room for the bearer token and cookies.
const MAX_BATCH_URL_LENGTH = 10_000

const getTrpcClient = (supabaseClient: WillieSupabaseClient) => {
  const headers = async () => {
    const { data: { session } } = await supabaseClient.auth.getSession()
    return {
      Authorization: `Bearer ${session?.access_token ?? ''}`,
      'X-W-Client': 'ops',
      'X-W-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  }
  const url = import.meta.env.VITE_TRPC_URL

  return createTRPCClient<AppRouter>({
    links: [httpBatchLink({ url, headers, maxURLLength: MAX_BATCH_URL_LENGTH })]
  })
}

type Props = PropsWithChildren<{
  queryClient: QueryClient
}>

export const TRPCProvider = ({ queryClient, children }: Props) => {
  const { supabaseClient } = useAuth()
  const trpcClient = useMemo(() => getTrpcClient(supabaseClient), [supabaseClient])

  return (
    <TRPCContextProvider trpcClient={trpcClient} queryClient={queryClient}>
      {children}
    </TRPCContextProvider>
  )
}

export { useTRPC, useTRPCClient }
